import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Modal,
  Form,
  Table,
  InputGroup,
  FormControl,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { FiPlus, FiEdit, FiDownload, FiUpload } from "react-icons/fi";
import { MdAddchart } from "react-icons/md";
import Pagination from "../Constant/Pagination";
import { Spinner } from "react-bootstrap";
import { MultiActionDropdown } from "../../components";
import { Link } from "react-router-dom";
import Block from "../../components/Block/Block";
import { FaUserTie } from "react-icons/fa";
import { MdWork } from "react-icons/md";
import {
  getAllDesignations,
  createDesignation,
  updateDesignation,
  uploadDesignationExcel,
} from "../../services/SettingServices";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";

const DesignationSettings = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState({ name: "" });
  const [editData, setEditData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const pageSize = 10;

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAllDesignations({
        page,
        page_size: pageSize,
        search,
      });
      setData(response.data.results || []);
      setCount(response.data.count || 0);
    } catch (err) {
      toast.error("Failed to fetch designations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(fetchData, 300);
    return () => clearTimeout(delay);
  }, [page, search]);

  const handleCreate = async () => {
    if (!formData.name.trim()) return toast.error("Name is required");
    try {
      await createDesignation(formData);
      fetchData();
      setFormData({ name: "" });
      setShowModal(false);
      toast.success("Designation created");
    } catch (err) {
      toast.error("Failed to create designation");
    }
  };

  const handleUpdate = async () => {
    if (!editData?.name?.trim()) return toast.error("Name is required");
    try {
      await updateDesignation(editData.id, editData);
      fetchData();
      setShowEditModal(false);
      toast.success("Designation updated");
    } catch (err) {
      toast.error("Failed to update designation");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadDesignationExcel(file);
      toast.success(res.data.message || "Upload successful");
      fetchData();
      setShowUploadModal(false);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await getAllDesignations({ export: true });
      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "designations_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Export successful!");
    } catch (err) {
      toast.error("Failed to export designations");
    }
  };

  return (
    <>
      <div className="card p-2 mb-2">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-1">
          <div className="flex-grow-1 flex-md-grow-0">
            <h5 className="d-flex mb-0 align-items-center gap-2">
              <FaUserTie size={18} className="text-primary"/>
              Designations
            </h5>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <DebounceSmallSearchInput
              handleSearchChange={(value) => setSearch(value)}
            />
            <MultiActionDropdown
              variant="outline-primary"
              btnText="Add"
              btnIcon={<MdAddchart />}
              btnOnClick={() => setShowModal(true)}
              menuOptions={[
                {
                  label: "Add",
                  icon: <FiPlus />,
                  onClick: () => setShowModal(true),
                },
                {
                  label: "Import",
                  icon: <FiUpload />,
                  onClick: () => setShowUploadModal(true),
                },
                {
                  label: "Export",
                  icon: <FiDownload />,
                  onClick: handleExport,
                },
              ]}
            />
          </div>
        </div>
      </div>
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "110px" }}
            >
              <Spinner animation="border" size={35} color="#255d5c" />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table fs-6 mb-0 align-middle">
              <thead className="table-light custom-thead">
                <tr>
                  <th>Name</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-center py-3">
                      No data found
                    </td>
                  </tr>
                ) : (
                  data.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.name}</td>
                      <td>
                        <FiEdit
                          size={14}
                          className="text-primary me-2"
                          title="Edit"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setEditData(row);
                            setShowEditModal(true);
                          }}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              </table>
            </div>
          )}

          <div className="d-flex justify-content-end">
            <Pagination
              currentPage={page}
              recordsPage={pageSize}
              dataLength={count}
              handlePagination={(selectedObj) =>
                setPage(selectedObj.selected + 1)
              }
              showPageInfo={false}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Add Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Designation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleCreate}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Designation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={editData?.name || ""}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdate}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Designations (Excel)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Excel File (.xlsx)</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              onChange={handleUpload}
              disabled={uploading}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DesignationSettings;
